import type { State } from "metabase/redux/store";

const LOADER_ID = "vizma-chat-integration-loader";
const ROOT_ID = "vizma-chat-loader-root";
const SESSION_ENDED_EVENT = "vizma-chat:parent-session-ended";
const SESSION_STARTED_EVENT = "vizma-chat:parent-session-started";
const BRIDGE_VERSION = "1";
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

interface AppStore {
  getState: () => State;
  subscribe: (listener: () => void) => () => void;
}

export interface VizmaChatConfig {
  origin: string;
  loaderUrl: string;
  widgetUrl: string;
  bootstrapUrl: string;
}

export function getVizmaChatConfig(): VizmaChatConfig | null {
  const bootstrap = window.MetabaseBootstrap as
    | Record<string, unknown>
    | undefined;
  const enabled = bootstrap?.["vizma-chat-enabled"];
  if (enabled !== true && enabled !== "true") {
    return null;
  }

  const rawOrigin = String(bootstrap?.["vizma-chat-origin"] ?? "").trim();
  let widgetOrigin: URL;
  try {
    widgetOrigin = new URL(rawOrigin);
  } catch {
    return null;
  }
  if (
    !["http:", "https:"].includes(widgetOrigin.protocol) ||
    widgetOrigin.username ||
    widgetOrigin.password ||
    (widgetOrigin.pathname !== "/" && widgetOrigin.pathname !== "") ||
    widgetOrigin.search ||
    widgetOrigin.hash
  ) {
    return null;
  }

  const parentOrigin = new URL(window.location.origin);
  const localDevelopment =
    widgetOrigin.protocol === "http:" &&
    parentOrigin.protocol === "http:" &&
    LOOPBACK_HOSTS.has(widgetOrigin.hostname) &&
    LOOPBACK_HOSTS.has(parentOrigin.hostname);
  if (widgetOrigin.protocol !== "https:" && !localDevelopment) {
    return null;
  }
  if (widgetOrigin.origin === parentOrigin.origin && !localDevelopment) {
    return null;
  }

  const bootstrapUrl = localDevelopment
    ? `${widgetOrigin.origin}/vizma-chat/bootstrap`
    : new URL(
        `${String(window.MetabaseRoot || "/").replace(/\/+$/, "")}/vizma-chat/bootstrap`,
        parentOrigin.origin,
      ).href;

  const release = bootstrap?.version as
    | { hash?: string; tag?: string }
    | undefined;
  const releaseId = String(release?.hash || release?.tag || "development")
    .replace(/[^A-Za-z0-9._-]/g, "-")
    .slice(0, 64);

  return {
    origin: widgetOrigin.origin,
    loaderUrl: `${widgetOrigin.origin}/vizma-chat/loader.v1.js?v=bi-${releaseId}`,
    widgetUrl: `${widgetOrigin.origin}/vizma-chat/widget`,
    bootstrapUrl,
  };
}

export function initializeVizmaChatIntegration(store: AppStore): () => void {
  const config = getVizmaChatConfig();
  if (!config || window.self !== window.top) {
    return () => undefined;
  }

  let previouslyAuthenticated = false;

  const sync = () => {
    const user = store.getState().currentUser;
    const authenticated = Boolean(user?.id);
    const routeVisible = isVisibleRoute();

    if (authenticated && routeVisible) {
      mountLoader(config, normalizeLocale(user?.locale), sync);
    }

    if (previouslyAuthenticated && !authenticated) {
      window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT));
    } else if (!previouslyAuthenticated && authenticated) {
      window.dispatchEvent(new CustomEvent(SESSION_STARTED_EVENT));
    }
    previouslyAuthenticated = authenticated;

    const root = document.getElementById(ROOT_ID);
    if (root) {
      root.hidden = !(authenticated && routeVisible);
    }
  };

  const unsubscribe = store.subscribe(sync);
  window.addEventListener("popstate", sync);
  sync();

  return () => {
    unsubscribe();
    window.removeEventListener("popstate", sync);
  };
}

function mountLoader(
  config: VizmaChatConfig,
  locale: string,
  onLoad: () => void,
): void {
  if (document.getElementById(LOADER_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = LOADER_ID;
  script.src = config.loaderUrl;
  script.async = true;
  script.dataset.vizmaChatEnabled = "true";
  script.dataset.vizmaChatWidgetUrl = config.widgetUrl;
  script.dataset.vizmaChatWidgetOrigin = config.origin;
  script.dataset.vizmaChatBootstrapUrl = config.bootstrapUrl;
  script.dataset.vizmaChatParentAuth = "vizma_bi";
  script.dataset.vizmaChatBridgeVersion = BRIDGE_VERSION;
  script.dataset.vizmaChatLocale = locale;
  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener(
    "error",
    () => console.warn("Vizma Chat launcher could not be loaded."),
    { once: true },
  );
  document.body.appendChild(script);
}

function isVisibleRoute(): boolean {
  const root = String(window.MetabaseRoot || "/").replace(/\/+$/, "");
  const pathname = window.location.pathname.slice(root.length) || "/";
  if (/^\/(auth|setup)(?:\/|$)/.test(pathname)) {
    return false;
  }
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  return !query.has("fullscreen") && !hash.has("fullscreen");
}

function normalizeLocale(value: string | null | undefined): string {
  const locale = String(value || "en")
    .toLowerCase()
    .replace("-", "_");
  if (locale.startsWith("ru")) {
    return "ru";
  }
  if (locale.startsWith("uz")) {
    return "uz_latn";
  }
  return "en";
}
